"use client";

import { createContext, useContext, useMemo, useState, ReactNode, useEffect } from 'react';

export type Density = 'comfortable' | 'compact';

type DensityContextType = {
  density: Density;
  setDensity: (d: Density) => void;
  rowPaddingClass: string; // helper for tables/lists
};

const DensityContext = createContext<DensityContextType | undefined>(undefined);

export function DensityProvider({ children, initial = 'comfortable' as Density }: { children: ReactNode; initial?: Density }) {
  const [density, setDensityState] = useState<Density>(initial);

  useEffect(() => {
    const saved = typeof window !== 'undefined' ? (localStorage.getItem('ui_density') as Density | null) : null;
    if (saved === 'comfortable' || saved === 'compact') setDensityState(saved);
  }, []);

  const setDensity = (d: Density) => {
    setDensityState(d);
    try { localStorage.setItem('ui_density', d); } catch {}
  };

  const value = useMemo(() => ({
    density,
    setDensity,
    rowPaddingClass: density === 'compact' ? 'py-2' : 'py-3',
  }), [density]);

  return <DensityContext.Provider value={value}>{children}</DensityContext.Provider>;
}

export function useDensity() {
  const ctx = useContext(DensityContext);
  if (!ctx) throw new Error('useDensity must be used within DensityProvider');
  return ctx;
}
